import {
  ActionIcon,
  type ActionIconProps,
  alpha,
  Button,
  type ButtonProps,
  darken,
  getThemeColor,
  lighten,
  useMantineTheme,
} from '@mantine/core'
import { ArrowLeftIcon, ArrowRightIcon } from '@phosphor-icons/react'
import type { ComponentPropsWithoutRef, ReactNode } from 'react'
import { resolveBoggleAccentCssColor } from './boggleAccentColor'
import { IconGlyph } from './Icons'

const BOGGLE_PRIMARY_GRADIENT_DEG = 165
/** Maps a single theme color into a slightly darker gradient end (similar to stepping two Mantine shades). */
const BOGGLE_PRIMARY_GRADIENT_DARKEN = 0.4

export function PrimaryButton({
  color = 'dark.7',
  bd,
  style,
  ...props
}: ButtonProps & ComponentPropsWithoutRef<'button'> & { color?: string }) {
  const theme = useMantineTheme()
  const from = getThemeColor(color, theme)
  const to = darken(from, BOGGLE_PRIMARY_GRADIENT_DARKEN)
  const bdColor = lighten(from, 0.2)
  const bg = `linear-gradient(${BOGGLE_PRIMARY_GRADIENT_DEG}deg, ${from} 0%, ${to} 100%)`
  const border = bd ?? `2px solid ${bdColor}`

  return (
    <Button
      variant="light"
      bdrs={50}
      bg={bg}
      bd={border}
      fw={400}
      fz={24}
      pb={1}
      h={66}
      px={26}
      style={style}
      {...props}
    />
  )
}

export function IconButton({
  color = 'dark.7',
  bd,
  size = 66,
  style,
  ...props
}: ActionIconProps & ComponentPropsWithoutRef<'button'> & { color?: string }) {
  const theme = useMantineTheme()
  const from = getThemeColor(color, theme)
  const to = darken(from, BOGGLE_PRIMARY_GRADIENT_DARKEN)
  const bdColor = lighten(from, 0.2)
  const bg = `linear-gradient(${BOGGLE_PRIMARY_GRADIENT_DEG}deg, ${from} 0%, ${to} 100%)`
  const border = bd ?? `2px solid ${bdColor}`

  return (
    <ActionIcon
      variant="light"
      bdrs={50}
      bg={bg}
      bd={border}
      size={size}
      style={style}
      {...props}
    />
  )
}

export function SecondaryButton({
  gradientColor = 'dark.8',
  ...props
}: ButtonProps & ComponentPropsWithoutRef<'button'> & { gradientColor?: string }) {
  return (
    <PrimaryButton
      color={gradientColor}
      bd="1px solid rgba(255, 255, 255, 0.2)"
      c="white"
      {...props}
    />
  )
}

/** Matches `ChallengeButton` border (--mantine-color-yellow-6). Use where rims should align with challenge Start. */
export const BOGGLE_CHALLENGE_BORDER_ACCENT_KEY = 'yellow.6'

export const ChallengeButton = Button.withProps({
  bd: '2px solid var(--mantine-color-yellow-6)',
  bg: 'linear-gradient(165deg, var(--mantine-color-yellow-6) 0%, var(--mantine-color-orange-8) 100%)',
  bdrs: 100,
  c: 'white',
  color: 'orange.8',
  fz: 24,
  h: 66,
  px: 16,
  pb: 1,
  fw: 400,
  variant: 'light',
})

export const PracticeButton = Button.withProps({
  bd: '1px solid rgba(255, 255, 255, 0.2)',
  bg: 'linear-gradient(165deg, var(--mantine-color-teal-6) 0%, var(--mantine-color-teal-8) 100%)',
  bdrs: 50,
  c: 'white',
  color: 'teal',
  fz: 18,
  h: 54,
  px: 16,
  variant: 'light',
})

export const QuietButton = Button.withProps({
  c: 'rgba(242, 232, 217, 0.9)',
  fz: 22,
  fw: 500,
  px: 8,
  variant: 'transparent',
  styles: {
    label: {
      color: 'inherit',
    },
  },
})

export function BackButton({
  children = 'Back',
  iconSize = 22,
  ...props
}: Omit<ButtonProps & ComponentPropsWithoutRef<'button'>, 'children' | 'leftSection'> & {
  children?: ReactNode
  iconSize?: number
}) {
  return (
    <QuietButton
      leftSection={<IconGlyph icon={ArrowLeftIcon} size={iconSize} />}
      type="button"
      {...props}
    >
      {children}
    </QuietButton>
  )
}

export function PlayButton({
  accentColor = 'green',
  disabled,
  label = 'Play',
  onClick,
  ...props
}: ButtonProps &
  ComponentPropsWithoutRef<'button'> & { accentColor?: string; label?: string }) {
  const theme = useMantineTheme()
  const accent = resolveBoggleAccentCssColor(accentColor, theme)
  const borderAccent = lighten(accent, 0.12)
  const disabledLabel = lighten(accent, 0.28)

  return (
    <Button
      bg={disabled ? 'transparent' : '#171e1b'}
      c={disabled ? disabledLabel : lighten(accent, 0.5)}
      disabled={disabled}
      fz={28}
      w={160}
      h="100%"
      onClick={onClick}
      px={16}
      bdrs={999}
      fw={600}
      rightSection={<IconGlyph icon={ArrowRightIcon} size={20} />}
      tt="uppercase"
      variant="light"
      {...props}
      style={{
        ...(!disabled
          ? {
              boxShadow: `0 0 8px 0 ${alpha(accent, 0.35)}`,
              border: `2px solid ${borderAccent}`,
            }
          : {
              background: 'transparent',
              boxShadow: 'none',
              border: '2px solid transparent',
            }),
        ...props.style,
      }}
    >
      {label}
    </Button>
  )
}
